/**
 * 로그인 페이지
 */
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { requestPasswordReset } from '../api/auth.api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Card } from '../components/ui/Card';
import { toast } from '../components/ui/Toast';
import { validateEmail } from '../utils/validation';
import GoogleLoginButton from '../components/auth/GoogleLoginButton';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const login = useAuthStore((state) => state.login);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const showResetSent = searchParams.get('reset') === 'sent';
  const [showResendReset, setShowResendReset] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [isResendLoading, setIsResendLoading] = useState(false);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resendEmail) return;
    setIsResendLoading(true);
    try {
      await requestPasswordReset(resendEmail);
      toast({ title: '재설정 링크를 재전송했습니다.', variant: 'success' });
      setShowResendReset(false);
      setResendEmail('');
    } catch (error: any) {
      toast({
        title: '재전송 실패',
        description: error.response?.data?.message || '잠시 후 다시 시도해주세요.',
        variant: 'destructive',
      });
    } finally {
      setIsResendLoading(false);
    }
  };

  const handleEmailBlur = () => {
    const result = validateEmail(email);
    if (!result.ok) setErrors((p) => ({ ...p, email: result.message }));
  };

  const handlePasswordBlur = () => {
    if (!password) setErrors((p) => ({ ...p, password: '비밀번호를 입력해주세요.' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const nextErrors: { email?: string; password?: string } = {};
    const emailResult = validateEmail(email);
    if (!emailResult.ok) nextErrors.email = emailResult.message;
    if (!password) nextErrors.password = '비밀번호를 입력해주세요.';
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      toast({
        title: '입력 오류',
        description: '이메일과 비밀번호를 올바르게 입력해주세요.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      await login(email, password);
      
      toast({
        title: '로그인 성공',
        description: '환영합니다!',
        variant: 'default',
      });
      
      // returnUrl이 있으면 해당 경로로, 없으면 홈으로 이동
      const returnUrl = searchParams.get('returnUrl');
      navigate(returnUrl && returnUrl.startsWith('/') ? returnUrl : '/');
    } catch (error: any) {
      console.error('로그인 에러:', error);
      
      toast({
        title: '로그인 실패',
        description: error.response?.data?.message || '이메일 또는 비밀번호가 올바르지 않습니다.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-dvh flex items-center justify-center bg-gradient-to-br from-primary-50 via-secondary-50/40 to-white dark:from-vinyl dark:via-primary-950 dark:to-vinyl px-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
      <Card className="w-full max-w-md p-8 shadow-2xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-slate-200 mb-2">MD STUDIO</h1>
          <p className="text-gray-600 dark:text-slate-400">로그인하여 앨범을 관리하세요</p>
        </div>

        {showResetSent && (
          <div className="mb-4 p-3 rounded-md bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 text-sm">
            <p className="font-medium">비밀번호 재설정 메일을 보냈습니다.</p>
            <p className="mt-1">
              받은 편지함을 확인하고 링크를 클릭해 주세요. 메일이 보이지 않으면 스팸함도 확인하거나{' '}
              <button
                type="button"
                onClick={() => setShowResendReset(true)}
                className="underline font-medium"
              >
                재전송
              </button>
              을 요청하세요.
            </p>
            {showResendReset && (
              <form onSubmit={handleResend} className="mt-3 flex gap-2">
                <Input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="이메일 주소"
                  className="flex-1 text-sm"
                  disabled={isResendLoading}
                  required
                  autoComplete="email"
                />
                <Button type="submit" disabled={isResendLoading} className="shrink-0 text-sm">
                  {isResendLoading ? '전송 중...' : '재전송'}
                </Button>
              </form>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} aria-label="로그인 폼" className="space-y-6">
          <div>
            <Label htmlFor="email" required className="block text-gray-700 dark:text-slate-300 mb-2">
              이메일
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors((p) => ({ ...p, email: undefined }));
              }}
              onBlur={handleEmailBlur}
              placeholder="your@email.com"
              className="w-full"
              disabled={isLoading}
              autoComplete="email"
              required
              error={errors.email}
            />
          </div>

          <div>
            <Label htmlFor="password" required className="block text-gray-700 dark:text-slate-300 mb-2">
              비밀번호
            </Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) setErrors((p) => ({ ...p, password: undefined }));
              }}
              onBlur={handlePasswordBlur}
              placeholder="••••••••"
              className="w-full"
              disabled={isLoading}
              autoComplete="current-password"
              required
              error={errors.password}
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? '로그인 중...' : '로그인'}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/reset-password')}
              className="text-sm text-primary-600 hover:text-primary-800 hover:underline"
            >
              비밀번호를 잊으셨나요?
            </button>
          </div>
        </form>

        {/* 구분선 */}
        <div className="mt-6 mb-6 flex items-center">
          <div className="flex-1 border-t border-gray-300 dark:border-slate-600"></div>
          <span className="px-4 text-sm text-gray-500 dark:text-slate-400">또는</span>
          <div className="flex-1 border-t border-gray-300 dark:border-slate-600"></div>
        </div>

        {/* Google 로그인 */}
        <GoogleLoginButton disabled={isLoading} />

        <div className="mt-6 text-center text-sm text-gray-600 dark:text-slate-400">
          계정이 없으신가요?{' '}
          <Link to="/signup" className="text-primary-600 hover:text-primary-700 font-medium">
            회원가입
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default LoginPage;

